import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import TabContent from './components/TabContent';
import ErrorBoundary from './components/common/ErrorBoundary';
import UserDashboard from './components/user/UserDashboard';
import TeacherDashboard from './components/teacher/TeacherDashboard';
import ParentDashboard from './components/parent/ParentDashboard';
import LandingPage from './components/LandingPage';
import SignupPage from './components/SignupPage';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AboutPage from './components/AboutPage';
import ContactPage from './components/ContactPage';
import BlogPage from './components/BlogPage';
import HelpCenterPage from './components/HelpCenterPage';
import ContactSupportPage from './components/ContactSupportPage';
import ResourcesPage from './components/ResourcesPage';
import BusinessPage from './components/business/BusinessPage';
import SupportSchoolSignup from './components/business/SupportSchoolSignup';
import AccreditedSchoolSignup from './components/business/AccreditedSchoolSignup';
import EducationPartnerSignup from './components/business/EducationPartnerSignup';
import BusinessDashboardRouter from './components/business/BusinessDashboardRouter';
import SupportSchoolDashboard from './components/business/SupportSchoolDashboard';
import AccreditedSchoolDashboard from './components/business/AccreditedSchoolDashboard';
import PrimarySchoolPage from './components/PrimarySchoolPage';
import MiddleSchoolPage from './components/MiddleSchoolPage';
import HighSchoolPage from './components/HighSchoolPage';
import HighSchoolStreamPage from './components/HighSchoolStreamPage';
import ModuleExamsPage from './components/ModuleExamsPage';
import ExploreCoursesPage from './components/ExploreCoursesPage';
import PartnersPage from './components/PartnersPage';
import PartnersApplyPage from './components/PartnersApplyPage';
import PartnersAgreementPage from './components/PartnersAgreementPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import TermsOfServicePage from './components/TermsOfServicePage';
import CookiePolicyPage from './components/CookiePolicyPage';
import RefundPolicyPage from './components/RefundPolicyPage';
import SchoolDashboard from './components/school/SchoolDashboard';
import logger from './utils/logger';
import { initializeFCM, registerFCMToken, onForegroundMessage } from './services/fcmService';
import TawkTo from './components/common/TawkTo';
import { syncThemeWithRoute } from './utils/theme';
import { installStudentSessionFetchGuard } from './services/api';

installStudentSessionFetchGuard();

function PublicHomepage() {
  return <LandingPage />;
}

function AccountThemeSync() {
  const { pathname } = useLocation();
  useEffect(() => {
    syncThemeWithRoute(pathname);
  }, [pathname]);
  return null;
}

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole }) => {
  // Use sessionStorage for tokens (more secure)
  // Fallback to localStorage for backward compatibility
  const token = sessionStorage.getItem('token') || sessionStorage.getItem('adminToken') || 
                localStorage.getItem('token') || localStorage.getItem('adminToken');
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  if (!token) {
    return <Navigate to="/" replace />;
  }

  // Check role (case-insensitive comparison)
  if (requiredRole) {
    const userRole = user.role?.toLowerCase();
    // Support both single role string and array of roles
    const requiredRoles = Array.isArray(requiredRole) 
      ? requiredRole.map(r => r.toLowerCase())
      : [requiredRole.toLowerCase()];
    
    if (!requiredRoles.includes(userRole)) {
      // If role doesn't match, redirect based on user's actual role
      if (userRole === 'admin' || userRole === 'agent' || userRole === 'moderator') {
        return <Navigate to="/admin-dashboard" replace />;
      } else if (userRole === 'teacher') {
        return <Navigate to="/teacher-dashboard" replace />;
      } else if (userRole === 'parent') {
        return <Navigate to="/parent-dashboard" replace />;
      } else if (userRole === 'school_admin') {
        return <Navigate to="/business/dashboard" replace />;
      } else if (userRole === 'school') {
        return <Navigate to="/school-dashboard" replace />;
      } else {
        return <Navigate to="/user-dashboard" replace />;
      }
    }
  }

  return children;
};

// Admin Dashboard Component
const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile sidebar toggle

  // Set default tab for agents and moderators
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const role = (user.role || '').toLowerCase();
    if (role === 'agent') {
      // Agents default to overview (dashboard home)
      setActiveTab('overview');
    } else if (role === 'moderator') {
      // Moderators default to pricing-billing
      setActiveTab('pricing-billing');
    }
  }, []);

  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    if (window.innerWidth <= 768) setSidebarOpen(false);
  };

  // Close sidebar on desktop and when clicking outside (mobile)
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!sidebarOpen || window.innerWidth > 768) return;
      const sidebar = document.querySelector('.sidebar');
      const hamburger = document.querySelector('.mobile-menu-toggle');
      if (sidebar && !sidebar.contains(event.target) && hamburger && !hamburger.contains(event.target)) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sidebarOpen]);

  const handleSearch = (query) => {
    // TODO: Implement global search functionality
    logger.log('Global search:', query);
    // Could navigate to search results page or filter current view
  };

  const handleNotificationClick = () => {
    // Navigate to full notifications page
    handleTabChange('notifications-alerts');
  };

  return (
    <ProtectedRoute requiredRole={['admin', 'agent', 'moderator']}>
      <ErrorBoundary>
        <div className="App">
          <div className="dashboard-container">
            <Sidebar activeTab={activeTab} onTabChange={handleTabChange} sidebarOpen={sidebarOpen} />
            <TopBar 
              onSearch={handleSearch} 
              onNotificationClick={handleNotificationClick}
              onTabChange={handleTabChange}
              onMenuToggle={() => setSidebarOpen((v) => !v)}
              sidebarOpen={sidebarOpen}
            />
            <div className="main-content">
              <ErrorBoundary>
                <TabContent activeTab={activeTab} onTabChange={handleTabChange} />
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    </ProtectedRoute>
  );
};

// User Dashboard Component
const UserDashboardRoute = () => {
  return (
    <ProtectedRoute>
      <ErrorBoundary name="user-dashboard">
        <UserDashboard />
      </ErrorBoundary>
    </ProtectedRoute>
  );
};

// Teacher Dashboard Component
const TeacherDashboardRoute = () => {
  return (
    <ProtectedRoute requiredRole="teacher">
      <TeacherDashboard />
    </ProtectedRoute>
  );
};

// Parent Dashboard Component
const ParentDashboardRoute = () => {
  return (
    <ProtectedRoute requiredRole="parent">
      <ParentDashboard />
    </ProtectedRoute>
  );
};

function App() {
  // Initialize FCM on app load
  useEffect(() => {
    initializeFCM().then((messaging) => {
      if (messaging) {
        // Get authentication token
        const token = sessionStorage.getItem('token') || 
                     sessionStorage.getItem('adminToken') || 
                     localStorage.getItem('token') || 
                     localStorage.getItem('adminToken');
        
        if (token) {
          registerFCMToken(token);
        }

        // Listen for foreground messages
        onForegroundMessage((payload) => {
          console.log('📬 Foreground notification received:', payload);
          
          // Show browser notification if permission granted
          if (payload.notification && 'Notification' in window) {
            if (Notification.permission === 'granted') {
              new Notification(payload.notification.title, {
                body: payload.notification.body,
                icon: '/logo192.png',
                badge: '/logo192.png'
              });
            }
          }
        });
      }
    }).catch((error) => {
      console.error('❌ Error initializing FCM:', error);
    });
  }, []);

  return (
    <Router>
      <AccountThemeSync />
      <TawkTo />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PublicHomepage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/help-center" element={<HelpCenterPage />} />
        <Route path="/contact-support" element={<ContactSupportPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/partners/apply" element={<PartnersApplyPage />} />
        <Route path="/partners/partner-agreement" element={<PartnersAgreementPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        <Route path="/cookie-policy" element={<CookiePolicyPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/resources" element={<ResourcesPage />} />
        <Route path="/business" element={<BusinessPage />} />
        <Route path="/business/signup/support" element={<SupportSchoolSignup />} />
        <Route path="/business/signup/accredited" element={<AccreditedSchoolSignup />} />
        <Route path="/business/signup/partner" element={<EducationPartnerSignup />} />
        <Route path="/business/dashboard" element={<BusinessDashboardRouter />} />
        <Route path="/business/dashboard/support" element={<SupportSchoolDashboard />} />
        <Route path="/business/dashboard/accredited" element={<AccreditedSchoolDashboard />} />
        <Route path="/explore-courses" element={<ExploreCoursesPage />} />
        
        {/* Resources - School Level Pages */}
        <Route path="/resources/primary" element={<PrimarySchoolPage />} />
        <Route path="/resources/middle" element={<MiddleSchoolPage />} />
        <Route path="/resources/high" element={<HighSchoolPage />} />
        
        {/* Resources - High School Stream Pages */}
        <Route path="/resources/high/:stream" element={<HighSchoolStreamPage />} />
        
        {/* Resources - Module Exams Pages */}
        <Route path="/resources/primary/:module" element={<ModuleExamsPage />} />
        <Route path="/resources/primary/:module/:year" element={<ModuleExamsPage />} />
        <Route path="/resources/middle/:module" element={<ModuleExamsPage />} />
        <Route path="/resources/middle/:module/:year" element={<ModuleExamsPage />} />
        <Route path="/resources/high/:stream/:module" element={<ModuleExamsPage />} />
        <Route path="/resources/high/:stream/:module/:year" element={<ModuleExamsPage />} />

        {/* Protected Dashboard Routes */}
        <Route path="/admin-dashboard/*" element={<AdminDashboard />} />
        <Route path="/user-dashboard/*" element={<UserDashboardRoute />} />
        <Route path="/teacher-dashboard/*" element={<TeacherDashboardRoute />} />
        <Route path="/parent-dashboard/*" element={<ParentDashboardRoute />} />
        <Route path="/school-dashboard/*" element={<ProtectedRoute requiredRole="school"><SchoolDashboard /></ProtectedRoute>} />

        {/* Fallback - redirect to landing page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
